import React, { useEffect, useState } from 'react';
import { FiCheckCircle, FiRefreshCw, FiX } from 'react-icons/fi';
import { useI18n } from '../i18n/I18nProvider';
import { parseManualPracticeItems } from '../lib/practiceAssignments';
import {
  IELTS_AUTHENTIC_FORMAT_VERSION,
  IELTS_DEFAULT_DURATION_SECONDS,
  IELTS_DEFAULT_TARGET_BAND,
  IELTS_MAX_DURATION_SECONDS,
  IELTS_MIN_DURATION_SECONDS,
  normalizeIeltsQuestionList,
  serializeIeltsSpeakingConfig,
} from '../lib/ieltsSpeaking';

const TYPES = [
  ['dictation', 'Dictation Loop', 'Strengthen spelling and meaning together.'],
  ['meaning_sprint', '7-Second Meaning', 'Build quick meaning recall.'],
  ['sudden_sprint', 'Lightning Challenge', 'Build speed and steady recall.'],
  ['pronunciation', 'Pronunciation Practice', 'Read short sentences and make the target word clear.'],
  ['adaptive_reading', 'Adaptive Reading', 'Transfer recent vocabulary into reading context.'],
  ['ielts_speaking', 'IELTS Speaking Practice', 'Record one answer, get a predicted band, and keep coaching history.'],
];

const PRACTICES_WITHOUT_VOCABULARY_SOURCE = new Set(['adaptive_reading', 'ielts_speaking']);

export default function AssignPracticeModal({
  isOpen,
  onClose,
  classId,
  student,
  classWide = false,
  audienceLabel = '',
  onAssigned,
}) {
  const { t } = useI18n();
  const [selected, setSelected] = useState(['ielts_speaking']);
  const [name, setName] = useState('');
  const [sourceText, setSourceText] = useState('');
  const [availableFrom, setAvailableFrom] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [readingMode, setReadingMode] = useState('custom');
  const [readingScope, setReadingScope] = useState('recent');
  const [ieltsTargetBand, setIeltsTargetBand] = useState(String(IELTS_DEFAULT_TARGET_BAND));
  const [ieltsSimulationMode, setIeltsSimulationMode] = useState('custom');
  const [ieltsCustomQuestion, setIeltsCustomQuestion] = useState('');
  const [ieltsCustomDuration, setIeltsCustomDuration] = useState(String(IELTS_DEFAULT_DURATION_SECONDS));
  const [ieltsPart1Questions, setIeltsPart1Questions] = useState('');
  const [ieltsPart2Topic, setIeltsPart2Topic] = useState('');
  const [ieltsPart2Points, setIeltsPart2Points] = useState('');
  const [ieltsPart2ShortFollowups, setIeltsPart2ShortFollowups] = useState('');
  const [ieltsPart3Questions, setIeltsPart3Questions] = useState('');
  const [ieltsFollowupMode, setIeltsFollowupMode] = useState('teacher_all');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setSelected(['ielts_speaking']);
    setName('');
    setSourceText('');
    setAvailableFrom('');
    setDueAt('');
    setReadingMode('custom');
    setReadingScope('recent');
    setIeltsTargetBand(String(IELTS_DEFAULT_TARGET_BAND));
    setIeltsSimulationMode('custom');
    setIeltsCustomQuestion('');
    setIeltsCustomDuration(String(IELTS_DEFAULT_DURATION_SECONDS));
    setIeltsPart1Questions('');
    setIeltsPart2Topic('');
    setIeltsPart2Points('');
    setIeltsPart2ShortFollowups('');
    setIeltsPart3Questions('');
    setIeltsFollowupMode('teacher_all');
    setSubmitting(false);
    setError('');
  }, [isOpen]);

  if (!isOpen) return null;

  const requiresVocabularySource = selected.some((value) => !PRACTICES_WITHOUT_VOCABULARY_SOURCE.has(value));
  const hasAudience = classWide ? Boolean(classId) : Boolean(student?._id);

  const submit = async () => {
    if (!selected.length || !hasAudience) {
      setError(t('Choose at least one practice.'));
      return;
    }
    if (requiresVocabularySource && !sourceText.trim()) {
      setError(t('Add vocabulary meanings for these practices, or use the vocabulary assignment flow first.'));
      return;
    }
    if (selected.includes('ielts_speaking')) {
      if (ieltsSimulationMode === 'custom' && !ieltsCustomQuestion.trim()) {
        setError(t('Add a speaking question.'));
        return;
      }
      if (ieltsSimulationMode === 'part1' && !normalizeIeltsQuestionList(ieltsPart1Questions).length) {
        setError(t('Add at least one Part 1 question.'));
        return;
      }
      if (ieltsSimulationMode === 'part2_3' && !ieltsPart2Topic.trim()) {
        setError(t('Add the Part 2 cue-card topic.'));
        return;
      }
      if (ieltsSimulationMode === 'part2_3' && ieltsFollowupMode !== 'adaptive' && !normalizeIeltsQuestionList(ieltsPart3Questions).length) {
        setError(t('Add at least one Part 3 follow-up question.'));
        return;
      }
    }
    setSubmitting(true);
    setError('');
    try {
      const ieltsConfig = ieltsSimulationMode === 'custom'
        ? serializeIeltsSpeakingConfig({
          question: ieltsCustomQuestion,
          part: '',
          suggested_duration_seconds: ieltsCustomDuration,
          strict_timer: true,
          target_band: ieltsTargetBand,
          format_version: IELTS_AUTHENTIC_FORMAT_VERSION,
          simulation_mode: 'custom',
        })
        : serializeIeltsSpeakingConfig({
          question: '',
          part: '',
          suggested_duration_seconds: ieltsSimulationMode === 'part1' ? 60 : 120,
          strict_timer: true,
          target_band: ieltsTargetBand,
          format_version: IELTS_AUTHENTIC_FORMAT_VERSION,
          simulation_mode: ieltsSimulationMode,
          part1_questions: normalizeIeltsQuestionList(ieltsPart1Questions),
          part2_cue_card: {
            topic: ieltsPart2Topic,
            bullet_points: normalizeIeltsQuestionList(ieltsPart2Points, 6),
          },
          part2_short_followups: normalizeIeltsQuestionList(ieltsPart2ShortFollowups, 4),
          part3_questions: normalizeIeltsQuestionList(ieltsPart3Questions),
          followup_mode: ieltsFollowupMode,
        });
      const response = await fetch('/api/admin/practice-assignments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          class_id: classId,
          ...(!classWide && student?._id ? { student_id: student._id } : {}),
          practice_options: selected.map((practice_type) => ({
            practice_type,
            config: practice_type === 'adaptive_reading'
              ? { reading_mode: readingMode, reading_scope: readingScope }
              : practice_type === 'ielts_speaking'
                ? ieltsConfig
                : {},
          })),
          title: name.trim() || undefined,
          config: {
            ...(availableFrom ? { available_from: new Date(availableFrom).toISOString() } : {}),
            ...(dueAt ? { due_at: new Date(dueAt).toISOString() } : {}),
          },
          source: requiresVocabularySource && sourceText.trim()
            ? {
                type: 'manual',
                items_snapshot: parseManualPracticeItems(sourceText),
              }
            : { type: 'none', items_snapshot: [] },
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || t('The practice could not be assigned.'));
      onAssigned?.(t('{count} practices assigned.', { count: (data.assignments || []).length }));
      onClose?.();
    } catch (submitError) {
      setError(submitError.message || t('The practice could not be assigned.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
      <div className="flex max-h-[calc(100vh-1rem)] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl sm:max-h-[calc(100vh-2rem)]">
        <header className="flex flex-none items-start justify-between border-b px-5 py-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">{t('Practice')}</div>
            <h2 className="mt-0.5 text-xl font-bold text-gray-900">{t('Assign practice')}</h2>
            <p data-i18n-skip className="mt-0.5 text-xs text-gray-500">
              {classWide
                ? t('Whole class: {name}', { name: audienceLabel })
                : (student?.nickname || student?.username)}
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={submitting} title={t('Close')} className="rounded p-2 text-gray-500 hover:bg-gray-100"><FiX className="text-xl" /></button>
        </header>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div className="grid gap-2 sm:grid-cols-2">
            {TYPES.map(([value, label, description]) => (
              <label key={value} className={`cursor-pointer rounded border px-3 py-2.5 ${selected.includes(value) ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 bg-white'}`}>
                <span className="flex items-start gap-2.5">
                  <input type="checkbox" checked={selected.includes(value)} onChange={() => setSelected((current) => current.includes(value) ? current.filter((item) => item !== value) : [...current, value])} className="mt-0.5 h-4 w-4 flex-none text-indigo-600" />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-gray-900">{t(label)}</span>
                    <span className="mt-0.5 block text-xs leading-5 text-gray-600">{t(description)}</span>
                  </span>
                </span>
              </label>
            ))}
          </div>
          {requiresVocabularySource && (
            <label className="block text-sm font-medium text-gray-800">
              {t('Vocabulary source')}
              <textarea value={sourceText} onChange={(event) => setSourceText(event.target.value)} className="mt-1 min-h-16 w-full resize-y rounded border border-gray-300 px-3 py-2" placeholder={t('One word per line')} />
              <span className="mt-1 block text-xs font-normal leading-5 text-gray-500">{t('For exact meanings, the vocabulary assignment flow is recommended.')}</span>
            </label>
          )}
          <label className="block text-sm font-medium text-gray-800">
            {t('Practice name')} <span className="font-normal text-gray-500">({t('optional')})</span>
            <input value={name} onChange={(event) => setName(event.target.value)} className="mt-1 w-full rounded border border-gray-300 px-3 py-2" placeholder={t('For example: Unit 4 practice')} />
            {selected.includes('ielts_speaking') && <span className="mt-1 block text-xs font-normal leading-5 text-gray-500">{t('Leave blank to generate a name from the question or cue-card topic.')}</span>}
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-medium text-gray-800">
              {t('Available from')} <span className="font-normal text-gray-500">({t('optional')})</span>
              <input type="datetime-local" value={availableFrom} onChange={(event) => setAvailableFrom(event.target.value)} className="mt-1 w-full rounded border border-gray-300 px-3 py-2" />
            </label>
            <label className="block text-sm font-medium text-gray-800">
              {t('Deadline')} <span className="font-normal text-gray-500">({t('optional')})</span>
              <input type="datetime-local" value={dueAt} onChange={(event) => setDueAt(event.target.value)} className="mt-1 w-full rounded border border-gray-300 px-3 py-2" />
            </label>
          </div>
          {selected.includes('adaptive_reading') && (
            <div className="grid gap-3 rounded border border-teal-200 bg-teal-50 p-3 sm:grid-cols-2">
              <label className="text-sm font-medium text-gray-800">{t('Reading mode')}<select value={readingMode} onChange={(event) => setReadingMode(event.target.value)} className="mt-1 w-full rounded border border-gray-300 bg-white p-2"><option value="custom">{t('Adaptive practice')}</option><option value="mock">{t('IELTS simulation')}</option></select></label>
              <label className="text-sm font-medium text-gray-800">{t('Vocabulary scope')}<select value={readingScope} onChange={(event) => setReadingScope(event.target.value)} className="mt-1 w-full rounded border border-gray-300 bg-white p-2"><option value="recent">{t('Recent words')}</option><option value="today">{t("Today's words")}</option><option value="last_session">{t('Last study session')}</option></select></label>
            </div>
          )}
          {selected.includes('ielts_speaking') && (
            <fieldset className="grid gap-3 rounded border border-violet-200 bg-violet-50 p-3 sm:grid-cols-2">
              <legend className="px-1 text-sm font-semibold text-violet-950">{t('IELTS speaking settings')}</legend>
              <p className="rounded border border-violet-200 bg-white px-3 py-2 text-xs leading-5 text-violet-950 sm:col-span-2">{t('Authentic mode reads each question aloud, plays a reminder sound, gives ten seconds to turn on the microphone, and stops each answer at the official practice limit.')}</p>
              <label className="block text-sm font-medium text-gray-800 sm:col-span-2">
                {t('Practice mode')}
                <select value={ieltsSimulationMode} onChange={(event) => setIeltsSimulationMode(event.target.value)} className="mt-1 w-full rounded border border-gray-300 bg-white p-2">
                  <option value="custom">{t('Custom practice')}</option>
                  <option value="part1">{t('Part 1 interview')}</option>
                  <option value="part2_3">{t('Part 2 cue card + Part 3 discussion')}</option>
                </select>
              </label>
              {ieltsSimulationMode === 'custom' ? (
                <>
                  <label className="block text-sm font-medium text-gray-800 sm:col-span-2">
                    {t('Speaking question')}
                    <textarea value={ieltsCustomQuestion} onChange={(event) => setIeltsCustomQuestion(event.target.value)} className="mt-1 min-h-28 w-full resize-y rounded border border-gray-300 bg-white px-3 py-2" placeholder={t('Enter one speaking question')} />
                    <span className="mt-1 block text-xs font-normal leading-5 text-gray-600">{t('The examiner reads this question aloud, then the student has ten seconds to turn on the microphone.')}</span>
                  </label>
                  <label className="block text-sm font-medium text-gray-800">
                    {t('Suggested answer time')}
                    <input type="number" min={IELTS_MIN_DURATION_SECONDS} max={IELTS_MAX_DURATION_SECONDS} step="1" value={ieltsCustomDuration} onChange={(event) => setIeltsCustomDuration(event.target.value)} className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2" />
                    <span className="mt-1 block text-xs font-normal text-gray-600">{t('15 to 300 seconds')}</span>
                  </label>
                   <p className="rounded border border-violet-200 bg-white px-3 py-2 text-xs leading-5 text-violet-950">
                     {t('Custom simulation uses strict timing and the examiner reads the question aloud before recording.')}
                   </p>
                </>
              ) : ieltsSimulationMode === 'part1' ? (
                <label className="block text-sm font-medium text-gray-800 sm:col-span-2">
                  {t('Part 1 questions')}
                  <textarea value={ieltsPart1Questions} onChange={(event) => setIeltsPart1Questions(event.target.value)} className="mt-1 min-h-32 w-full resize-y rounded border border-gray-300 bg-white px-3 py-2" placeholder={t('One question per line')} />
                  <span className="mt-1 block text-xs font-normal text-gray-600">{t('Each answer can end early and is cut off after 60 seconds.')}</span>
                </label>
              ) : (
                <>
                  <label className="block text-sm font-medium text-gray-800 sm:col-span-2">
                    {t('Part 2 cue-card topic')}
                    <textarea value={ieltsPart2Topic} onChange={(event) => setIeltsPart2Topic(event.target.value)} className="mt-1 min-h-20 w-full resize-y rounded border border-gray-300 bg-white px-3 py-2" placeholder={t('Describe a person, place, object, or experience...')} />
                  </label>
                  <label className="block text-sm font-medium text-gray-800 sm:col-span-2">
                    {t('Cue-card points')} <span className="font-normal text-gray-500">({t('optional')})</span>
                    <textarea value={ieltsPart2Points} onChange={(event) => setIeltsPart2Points(event.target.value)} className="mt-1 min-h-24 w-full resize-y rounded border border-gray-300 bg-white px-3 py-2" placeholder={t('One point per line')} />
                  </label>
                  <label className="block text-sm font-medium text-gray-800 sm:col-span-2">
                    {t('Short Part 2 follow-ups')} <span className="font-normal text-gray-500">({t('optional')})</span>
                    <textarea value={ieltsPart2ShortFollowups} onChange={(event) => setIeltsPart2ShortFollowups(event.target.value)} className="mt-1 min-h-20 w-full resize-y rounded border border-gray-300 bg-white px-3 py-2" placeholder={t('One question per line')} />
                  </label>
                  <label className="block text-sm font-medium text-gray-800 sm:col-span-2">
                    {t('Part 3 follow-up mode')}
                    <select value={ieltsFollowupMode} onChange={(event) => setIeltsFollowupMode(event.target.value)} className="mt-1 w-full rounded border border-gray-300 bg-white p-2">
                      <option value="teacher_all">{t('Ask all teacher questions')}</option>
                      <option value="random_two">{t('Randomly ask two teacher questions')}</option>
                      <option value="adaptive">{t('Automatic examiner follow-up')}</option>
                    </select>
                  </label>
                   {ieltsFollowupMode !== 'adaptive' ? (
                     <label className="block text-sm font-medium text-gray-800 sm:col-span-2">
                       {t('Part 3 questions')}
                       <textarea value={ieltsPart3Questions} onChange={(event) => setIeltsPart3Questions(event.target.value)} className="mt-1 min-h-28 w-full resize-y rounded border border-gray-300 bg-white px-3 py-2" placeholder={t('One question per line')} />
                     </label>
                   ) : (
                     <p className="rounded border border-violet-200 bg-white px-3 py-2 text-xs leading-5 text-violet-950 sm:col-span-2">
                       {t('Automatic examiner follow-ups are generated from the student’s real answer during the simulation. No seed question is needed.')}
                     </p>
                   )}
                </>
              )}
              <label className="block text-sm font-medium text-gray-800 sm:col-span-2">
                {t('Target band')}
                <input type="number" min="0" max="9" step="0.5" value={ieltsTargetBand} onChange={(event) => setIeltsTargetBand(event.target.value)} className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 sm:max-w-48" />
                <span className="mt-1 block text-xs font-normal text-gray-600">{t('The result will show whether the student reached this predicted band.')}</span>
              </label>
              {ieltsSimulationMode !== 'custom' && <p className="text-xs leading-5 text-violet-900 sm:col-span-2">{t('The question order and random selection are frozen for each attempt so a resumed attempt stays consistent.')}</p>}
            </fieldset>
          )}
          <p className="text-xs text-gray-500">{t('The student list is frozen at the moment you assign this practice.')}</p>
          {error && <div className="rounded border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800">{error}</div>}
        </div>
        <footer className="flex flex-none justify-end gap-3 border-t bg-gray-50 px-5 py-3">
          <button type="button" onClick={onClose} disabled={submitting} className="rounded border border-gray-300 px-4 py-2">{t('Cancel')}</button>
          <button type="button" onClick={submit} disabled={submitting} className="inline-flex items-center gap-2 rounded bg-indigo-600 px-5 py-2 font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">{submitting ? <FiRefreshCw className="animate-spin" /> : <FiCheckCircle />}{submitting ? t('Assigning...') : t('Assign selected practice')}</button>
        </footer>
      </div>
    </div>
  );
}
